import { useContext, useEffect } from 'react';

import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { useRouter } from '@eneco/next';
import { GrowthBookContext } from '@growthbook/growthbook-react';

import { FlowConsumerContextEnergy } from '../../../../context';
import { Offer } from '../../../../shared/types';
import { touExperimentFeatureToggleKey, touExperimentProductFilter } from '../../../growthbook/experiment.helpers';

export const promoteTimeOfUseOfferFeatureKey = 'exp-n-657-promote-time-of-use-offer';
export const promoteTimeOfUseOfferVariant = 'B';

/** Promotion code shown when a visitor is kept out of the Time of Use experiment. */
export const excludedTouRecommendedOffer = 'POAA12CExxxxxC';

const touOffersConfig = {
  experimentKey: touExperimentFeatureToggleKey,
  filterPredicate: touExperimentProductFilter,
};

export type TouOffersConfig = {
  experimentKey: string;
  filterPredicate: (offer: Offer, variant: string | null) => boolean;
} | null;

export type UseTouOffersConfigResult = {
  getOffersConfig: TouOffersConfig;
  /**
   * Set on this render when the promotion should show, so the offer request can
   * send it before the flow context update from the effect below has landed.
   */
  recommendedOffer?: string;
};

/**
 * exp-n-657 — Time of Use offer filtering.
 *
 * Runs only for variant B of `exp-n-657-promote-time-of-use-offer`. In that variant, a visitor with
 * `showPromotionDialog` and a double meter skips the Time of Use filter and stores
 * {@link excludedTouRecommendedOffer} on the flow context. Every other variant keeps the normal filter
 * and leaves `recommendedOffer` unchanged.
 * The feature is only read when `showPromotionDialog` is set and the visitor has a double meter, so
 * everyone else is not exposed and stays in the Time of Use filter. Skipping the filter also skips its
 * `getFeatureValue`.
 * `recommendedOffer` is returned on this render because the offer request goes out before the effect
 * can store it on the flow context.
 * Delete this hook (and its call sites) to always apply the filter.
 */
export const useTouOffersConfig = (): UseTouOffersConfigResult => {
  const { query } = useRouter();
  const { growthbook } = useContext(GrowthBookContext);
  const { useFlowSelector, useFlowActorRef } = useFlowHooks<FlowConsumerContextEnergy>();
  const showPromotionDialog = useFlowSelector(state => state.context.showPromotionDialog);
  const hasDoubleMeter = useFlowSelector(state => state.context.hasDoubleMeter);
  const recommendedOffer = useFlowSelector(state => state.context.recommendedOffer);
  const sendToFlowMachine = useFlowActorRef().send;

  const shouldShowPromotionDialog =
    Boolean(showPromotionDialog) &&
    Boolean(hasDoubleMeter) &&
    query.disableExperiments !== 'true' &&
    growthbook?.getFeatureValue(promoteTimeOfUseOfferFeatureKey, null) === promoteTimeOfUseOfferVariant;

  useEffect(() => {
    if (!shouldShowPromotionDialog || recommendedOffer === excludedTouRecommendedOffer) return;

    sendToFlowMachine({
      type: 'UPDATE_VALUES',
      values: { recommendedOffer: excludedTouRecommendedOffer },
    });
  }, [shouldShowPromotionDialog, recommendedOffer, sendToFlowMachine]);

  if (shouldShowPromotionDialog) {
    return { getOffersConfig: null, recommendedOffer: excludedTouRecommendedOffer };
  }

  return { getOffersConfig: touOffersConfig };
};
